import Error from 'next/error';
import qs from 'query-string';
import { NextRouter, useRouter } from 'next/router';
import { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';

// Components
import { Layout } from 'components/common';
import { ProductCard, ProductFilter, ProductSorting, PromotedProduct } from 'components/product';
import { Pagination } from 'components/ui';
import { Breadcrumbs, BreadcrumbItem } from 'components/ui';

// Interfaces
import { FilterItem } from 'interfaces/Storm/Filter';
import { ProductList as ProductListType } from 'interfaces/Storm/Product';
import { PageError } from 'interfaces/error';
import { CfCategory } from 'interfaces/Contentful/CfCategory';
import { ProductDetail } from 'interfaces/Storm/ProductDetail';

// Utils
import { createSlugFromParams } from 'utils/routes';
import getCategoryName from 'utils/getCategoryName';
import { convertReqToBearerToken } from 'utils/authUtil';

// Services
import { getProductByPartNo, getProductsAndFiltersByCategorySeedV2 } from 'services/storm';
import { getContentfulCategoryPage, getContentfulSettings } from 'services/contentful';

// Hooks
import useProductsWithCustomerPrices from 'hooks/useProductsWithCustomerPrices';

interface Props {
  error?: PageError;
  products?: ProductListType;
  filters?: FilterItem[];
  pageNo?: number;
  amountOfPages?: number;
  content?: CfCategory;
  promotedProduct?: ProductDetail;
}

const getQueryParams = (router: NextRouter) => qs.parseUrl(router.asPath).query;

const CategoryPage: NextPage<Props> = ({
  products,
  filters,
  error,
  pageNo,
  amountOfPages,
  content,
  promotedProduct,
}) => {
  const router = useRouter();
  const { products: productsWithUserPrices, pricesAreLoading } = useProductsWithCustomerPrices(products?.items);

  const handleOnPaginationChange = (page: number): void => {
    const sortByParams = {
      pageNo: page,
    };
    const originalParams = getQueryParams(router);

    const newParams = { ...originalParams, ...sortByParams };

    const searchString = qs.stringify(newParams, { skipNull: true, arrayFormat: 'comma' });
    const slug = Array.isArray(router.query.slug) ? router.query.slug.join('/') : router.query.slug;
    const asPath = `/category/${slug}${searchString.length > 0 ? '?' + searchString : ''}`;

    if (asPath !== router.asPath) {
      router.push({ pathname: router.pathname, query: searchString }, asPath);
    }
  };

  if (error) {
    return <Error statusCode={error.statusCode} title={error.message} />;
  }

  if (!content || content === null) {
    return <Error statusCode={404} title={'No page was found'} />;
  }

  const categoryHeaderClasses = cn('container max-width-lg text-component text-sm', {
    'margin-top-md': promotedProduct,
    'margin-top-xl@md': !promotedProduct,
  });

  const createBreadcrumbs = ({ title, slug }: CfCategory) => {
    const titleArray = title.split('/');
    const slugArray = slug.split('/');

    const breadcrumbLinks = titleArray.map((title, index) => {
      const slug = slugArray.reduce((acc, current, reduceIndex) => {
        if (reduceIndex > index) return acc;
        return `${acc}/${current}`;
      }, '/category');

      return { title: title, href: slug, as: slug };
    });

    return breadcrumbLinks;
  };
  const breadcrumbs = [
    {
      title: 'Start',
      href: '/',
      as: '/',
    },
  ];

  content && breadcrumbs.push(...createBreadcrumbs(content));

  return (
    <div>
      <NextSeo
        title={content.title}
        description={content?.description}
        openGraph={{
          type: 'website',
          title: content.title,
          description: content?.description,
        }}
      />

      {promotedProduct && (
        <PromotedProduct product={promotedProduct} containerWidth='max-width-lg' useBackgroundColor={true} />
      )}

      <div className='container max-width-lg margin-y-sm'>
        {breadcrumbs && (
          <Breadcrumbs>
            {breadcrumbs.map((b) => (
              <BreadcrumbItem key={b.href} href={b.href}>
                {b.title}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
        )}
      </div>

      <div className={categoryHeaderClasses}>
        <h1>{getCategoryName(content.title)}</h1>
      </div>

      <div className={'container max-width-lg margin-y-lg margin-y-xl@xl'}>
        <div className={'grid gap-md'}>
          <aside className={'col-12 col-4@md col-3@xl'}>
            {filters && <ProductFilter filters={filters} defaultOpen={false} />}
          </aside>
          <div className={'col-12 col-8@md col-9@xl'}>
            {products && (
              <div className={'flex items-center justify-between margin-bottom-sm'}>
                <p className={'text-sm'}>
                  <FormattedMessage
                    id='categoryPage.numberOfProducts'
                    description={'Number of products in the listing'}
                    defaultMessage='{itemCount} products'
                    values={{ itemCount: products.itemCount }}
                  />
                </p>
                <ProductSorting />
              </div>
            )}
            {productsWithUserPrices && (
              <div className={'parent grid gap-sm'}>
                {productsWithUserPrices.map((p) => (
                  <div key={p.partNo} className={'col-12 col-6@xs col-4@md'}>
                    <ProductCard product={p} pricesAreLoading={pricesAreLoading} />
                  </div>
                ))}
              </div>
            )}
            {pageNo && amountOfPages && (
              <div className={'margin-top-md margin-top-xl@lg'}>
                <Pagination totalPages={amountOfPages} currentPage={pageNo} onPageChange={handleOnPaginationChange} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, locale } = context;
  const { slug, ...rest } = query;

  if (!query || !slug) {
    return {
      props: {
        error: {
          statusCode: 404,
          message: 'No slug was given to the page',
        },
      },
    };
  }

  const url = createSlugFromParams(slug);
  const cfResponse = await getContentfulCategoryPage(url, locale);
  const settings = await getContentfulSettings(locale);

  if (!cfResponse.data) {
    return {
      props: {
        error: {
          statusCode: 404,
          message: `No page could be found with slug ${url}`,
        },
      },
    };
  }

  let promotedProduct: ProductDetail | null = null;
  if (typeof cfResponse.data.promotedProduct === 'string') {
    const response = await getProductByPartNo(cfResponse.data.promotedProduct, {
      headers: {
        Authorization: 'Bearer ' + convertReqToBearerToken(context.req),
      },
    });
    promotedProduct = response.data;
  }
  const {
    data: { products, filters, pageNo, amountOfPages },
  } = await getProductsAndFiltersByCategorySeedV2(cfResponse.data.stormCategoryId, rest, {
    headers: {
      Authorization: 'Bearer ' + convertReqToBearerToken(context.req),
    },
  });

  return {
    props: {
      settings: settings.data,
      products,
      filters,
      pageNo,
      amountOfPages,
      content: cfResponse.data,
      promotedProduct: promotedProduct,
    },
  };
};

// @ts-ignore custom implementation that NextPage doesn't cover
CategoryPage.Layout = Layout;

export default CategoryPage;
